/**
 * Video Monthly Trend Chart
 * ==========================
 * Shows video approval trend over the last 6 months.
 * Line chart with monthly breakdown.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { userManagementApi } from '../../api/userManagement';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg border border-gray-200 text-xs">
        <p className="font-bold text-gray-800 mb-1">{data.month_full}</p>
        <p style={{ color: EMZI_COLORS.primary }}>
          🎬 Video Approved: <span className="font-bold">{data.count}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Trend indicator
const TrendIndicator = ({ trend }) => {
  const isPositive = trend > 0;
  const isZero = trend === 0;
  
  return (
    <span className={`text-xs font-bold flex items-center gap-0.5 ${
      isZero ? 'text-gray-500' : isPositive ? 'text-green-600' : 'text-red-500'
    }`}>
      {isZero ? '→' : isPositive ? '📈' : '📉'}
      {isZero ? '0%' : `${isPositive ? '+' : ''}${trend}%`}
    </span>
  );
};

const VideoMonthlyTrend = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userManagementApi.getVideoMonthlyTrend();
      setData(response);
    } catch (err) {
      console.error('Failed to load monthly trend:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="mt-3" style={{ borderColor: EMZI_COLORS.border }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <div 
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${EMZI_COLORS.primary} transparent` }}
            ></div>
            <span className="ml-2 text-sm text-gray-500">Loading trend...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-3" style={{ borderColor: EMZI_COLORS.error }}>
        <CardContent className="p-4 text-center">
          <p className="text-sm text-red-500 mb-2">{error}</p>
          <Button onClick={loadData} size="sm" style={{ backgroundColor: EMZI_COLORS.primary }}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.monthly_data || [];
  const summary = data?.summary || {};

  return (
    <Card className="mt-3 overflow-hidden" style={{ borderColor: EMZI_COLORS.border }}>
      {/* Header */}
      <CardHeader className="px-3 py-2 border-b bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            📅 Video Approval Bulanan
            <TrendIndicator trend={summary.overall_trend || 0} />
          </CardTitle>
          <Badge 
            className="text-xs px-2"
            style={{ backgroundColor: EMZI_COLORS.primary, color: 'white' }}
          >
            6 Bulan
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-3">
        {/* Summary Stats Row - Compact */}
        <div className="flex gap-1 mb-2">
          <div 
            className="flex-1 p-1.5 rounded-lg text-center"
            style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}
          >
            <div className="text-base font-black text-blue-600">{summary.total || 0}</div>
            <div className="text-[9px] text-gray-600">Total</div>
          </div>
          <div 
            className="flex-1 p-1.5 rounded-lg text-center"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
          >
            <div className="text-base font-black text-green-600">{summary.average || 0}</div>
            <div className="text-[9px] text-gray-600">Purata</div>
          </div>
          <div 
            className="flex-1 p-1.5 rounded-lg text-center"
            style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}
          >
            <div className="text-base font-black text-amber-600">{summary.best_month?.count || 0}</div>
            <div className="text-[9px] text-gray-600">Max</div>
          </div>
        </div>

        {/* Area Chart - Compact */}
        <div className="bg-gray-50 rounded-xl p-2">
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart 
              data={chartData} 
              margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={EMZI_COLORS.primary} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={EMZI_COLORS.primary} stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 9, fill: '#666' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis 
                tick={{ fontSize: 9, fill: '#666' }}
                axisLine={{ stroke: '#E5E7EB' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone"
                dataKey="count" 
                stroke={EMZI_COLORS.primary}
                strokeWidth={2}
                fill="url(#colorApproved)"
                dot={{ fill: EMZI_COLORS.primary, strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, fill: EMZI_COLORS.primary }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Breakdown - Single Row */}
        <div className="mt-3 flex gap-1">
          {chartData.map((item, index) => (
            <div 
              key={`item-${index}`}
              className="flex-1 text-center p-1.5 rounded-lg"
              style={{ 
                backgroundColor: item.count === summary.best_month?.count && item.count > 0
                  ? '#FEF3C7' 
                  : '#F9FAFB'
              }}
            >
              <div 
                className="text-sm font-bold"
                style={{ color: item.count > 0 ? EMZI_COLORS.primary : '#9CA3AF' }}
              >
                {item.count}
              </div>
              <div className="text-[9px] text-gray-500">{item.month}</div>
            </div>
          ))}
        </div>

        {/* Insight */}
        {summary.best_month && summary.best_month.count > 0 && (
          <div 
            className="mt-3 p-2 rounded-lg text-xs"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
          >
            <span className="font-semibold">🏆 Best Month:</span>
            {' '}
            {summary.best_month.month_full} dengan {summary.best_month.count} video approved.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoMonthlyTrend;
